import _ from "lodash";
import { apiPatch } from "../Api/apiCall";
import {
  getUpdateData,
  persistAddress,
  persistCourt,
  persistPerson,
  sanitizeOrganizationData,
  sanitizeOrganizationEmailData,
} from "./persisterService";
import { Organization } from "../../types/Organization";
import ApiClient from "../ApiClient";
import { persistAbstractCaseSubEntities } from "./AbstractCasePersister";
import { UseForm } from "../../types/UseForm";
import { LitigationUnion } from "../../types/LitigationUnion";

export const persistLitigationSubEntities = async (updateData: UseForm["values"], origLitigation: LitigationUnion) => {
  await persistAbstractCaseSubEntities(updateData, origLitigation);

  if (_.isObject(updateData.clientLegalRepresentative)) {
    const clientLegalRepresentativeUpdateData = getUpdateData(
      updateData.clientLegalRepresentative,
      origLitigation,
      "clientLegalRepresentative"
    );
    await persistPerson(origLitigation.clientLegalRepresentative.id, clientLegalRepresentativeUpdateData);
  }

  if (
    _.has(updateData, "clientLegalRepresentative.residenceAddress") &&
    _.isObject(updateData.clientLegalRepresentative.residenceAddress)
  ) {
    const clientLegalRepresentativeAddressUpdateData = getUpdateData(
      updateData.clientLegalRepresentative.residenceAddress,
      origLitigation,
      "clientLegalRepresentative.residenceAddress"
    );
    await persistAddress(
      origLitigation.clientLegalRepresentative.residenceAddress.id,
      clientLegalRepresentativeAddressUpdateData
    );
  }

  if (_.isObject(updateData.secondClientLegalRepresentative)) {
    const secondClientLegalRepresentativeUpdateData = getUpdateData(
      updateData.secondClientLegalRepresentative,
      origLitigation,
      "secondClientLegalRepresentative"
    );
    await persistPerson(origLitigation.secondClientLegalRepresentative.id, secondClientLegalRepresentativeUpdateData);
  }

  if (
    _.has(updateData, "secondClientLegalRepresentative.residenceAddress") &&
    _.isObject(updateData.secondClientLegalRepresentative.residenceAddress)
  ) {
    const secondClientLegalRepresentativeAddressUpdateData = getUpdateData(
      updateData.secondClientLegalRepresentative.residenceAddress,
      origLitigation,
      "secondClientLegalRepresentative.residenceAddress"
    );
    await persistAddress(
      origLitigation.secondClientLegalRepresentative.residenceAddress.id,
      secondClientLegalRepresentativeAddressUpdateData
    );
  }

  if (origLitigation.opposingPartyPerson && _.isObject(updateData.opposingPartyPerson)) {
    const opposingPartyPersonUpdateData = getUpdateData(
      updateData.opposingPartyPerson,
      origLitigation,
      "opposingPartyPerson"
    );
    await persistPerson(origLitigation.opposingPartyPerson.id, opposingPartyPersonUpdateData);
  }

  if (
    origLitigation.opposingPartyPerson &&
    _.has(updateData, "opposingPartyPerson.residenceAddress") &&
    _.isObject(updateData.opposingPartyPerson.residenceAddress)
  ) {
    const opposingPartyPersonAddressUpdateData = getUpdateData(
      updateData.opposingPartyPerson.residenceAddress,
      origLitigation,
      "opposingPartyPerson.residenceAddress"
    );
    await persistAddress(origLitigation.opposingPartyPerson.residenceAddress.id, opposingPartyPersonAddressUpdateData);
  }

  if (
    _.isObject(updateData.opposingPartyLegalEntity?.organization)
  ) {
    const opposingParty: Partial<Organization> = updateData.opposingPartyLegalEntity.organization;
    let opposingPartyLegalEntityUpdateData = getUpdateData(
      updateData.opposingPartyLegalEntity.organization,
      origLitigation,
      "opposingPartyLegalEntity.organization"
    );

    opposingPartyLegalEntityUpdateData.additionalRepresentatives = opposingParty.additionalRepresentatives;

    await ApiClient.patch(`organizations/${origLitigation.opposingPartyLegalEntity.organization.id}`, {
      body: JSON.stringify(sanitizeOrganizationData(opposingPartyLegalEntityUpdateData)),
    });
  }

  if (
    _.isObject(updateData.opposingPartyLegalEntity?.organization.address)
  ) {
    const opposingPartyLegalEntityAddressUpdateData = getUpdateData(
      updateData.opposingPartyLegalEntity.organization.address,
      origLitigation,
      "opposingPartyLegalEntity.organization.address"
    );
    await persistAddress(
      origLitigation.opposingPartyLegalEntity.organization.address.id,
      opposingPartyLegalEntityAddressUpdateData
    );
  }

  if (
    _.isObject(updateData.opposingPartyLegalEntity?.organization.mainEmail)
  ) {
    const opposingPartyLegalEntityEmailUpdateData = getUpdateData(
      updateData.opposingPartyLegalEntity.organization.mainEmail,
      origLitigation,
      "opposingPartyLegalEntity.organization.mainEmail"
    );

    await ApiClient.patch(`organization_emails/${origLitigation.opposingPartyLegalEntity.organization.mainEmail.id}`, {
      body: JSON.stringify(sanitizeOrganizationEmailData(opposingPartyLegalEntityEmailUpdateData)),
    });
  }

  if (origLitigation.debtCollectionAgency && _.isObject(updateData.debtCollectionAgency)) {
    const debtCollectionAgencyCaseOrganizationUpdateData = getUpdateData(
      updateData.debtCollectionAgency,
      origLitigation,
      "debtCollectionAgency"
    );

    await ApiClient.patch(`case_organizations/${origLitigation.debtCollectionAgency.id}`, {
      body: JSON.stringify(debtCollectionAgencyCaseOrganizationUpdateData),
    });
  }

  if (origLitigation.responsibleCourt && _.isObject(updateData.responsibleCourt)) {
    const courtUpdateData = getUpdateData(updateData.responsibleCourt, origLitigation, "responsibleCourt");
    !_.isEmpty(courtUpdateData) && (await persistCourt(origLitigation.responsibleCourt.id, courtUpdateData));
  }

  if (_.isObject(updateData.opponentLawyer)) {
    const opponentLawyerUpdateData = getUpdateData(updateData.opponentLawyer, origLitigation, "opponentLawyer");
    await persistPerson(origLitigation.opponentLawyer.id, opponentLawyerUpdateData);
  }

  if (_.isObject(updateData.opponentLawFirm?.organization)) {
    const opponentLawFirmUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization,
      origLitigation,
      "opponentLawFirm.organization"
    );
    await apiPatch("organizations", origLitigation.opponentLawFirm.organization.id, opponentLawFirmUpdateData);
  }

  if (
    _.isObject(updateData.opponentLawFirm?.organization.mainEmail)
  ) {
    const opponentLawFirmEmailUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization.mainEmail,
      origLitigation,
      "opponentLawFirm.organization.mainEmail"
    );

    await ApiClient.patch(`organization_emails/${origLitigation.opponentLawFirm.organization.mainEmail.id}`, {
      body: JSON.stringify(sanitizeOrganizationEmailData(opponentLawFirmEmailUpdateData)),
    });
  }

  if (
    _.has(updateData, "opponentLawFirm.organization.address") &&
    _.isObject(updateData.opponentLawFirm.organization.address)
  ) {
    const opponentLawFirmAddressUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization.address,
      origLitigation,
      "opponentLawFirm.organization.address"
    );
    await persistAddress(origLitigation.opponentLawFirm.organization.address.id, opponentLawFirmAddressUpdateData);
  }
};
